/* eslint-disable require-jsdoc */
import * as React from 'react';
import {ThemeProvider, createTheme} from '@mui/material';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import Modal from '@mui/material/Modal';

import CustomMaterialTable from '../../CustomMaterialTable';

interface RowProps {
  row: TelemedicineCharges.RowData,
}

// https://cloud.google.com/functions/pricing?hl=pt-br
const calculateGCPIntegrationCost = ({
  totalIntegrationAPICalls,
  totalIntegrationAPICallsDurationInMs,
  totalIntegrationAPICallsContentLengthInBytes,
}: TelemedicineCharges.RowData) => {
  const invocationCost = 0.0000004;
  const gigabytePerSecondCost = 0.0000035;
  const CPUPerSecondCost = 0.0000140;
  const networkTransferPerGigabyteCost = 0.12;

  const totalContentLengthInGigabytes = totalIntegrationAPICallsContentLengthInBytes / 1000000000;

  const functionsCost = Number(
      (
        totalIntegrationAPICalls * (
          invocationCost +
        (
          (totalIntegrationAPICallsDurationInMs / 1000) * (gigabytePerSecondCost + CPUPerSecondCost))
        ) +
          totalContentLengthInGigabytes * networkTransferPerGigabyteCost
      ).toFixed(2),
  );

  const hostingTransferPerGigabyteCost = 0.15;

  const hostingCost = Number(
      (totalContentLengthInGigabytes * hostingTransferPerGigabyteCost).toFixed(2),
  );

  return Number((functionsCost + hostingCost).toFixed(2));
};

const calculateChimeCost = (
    row: TelemedicineCharges.RowData,
): number => {
  return Number(
      (2 * row.averageAnamnesisDurationInMinutes * row.totalStartedAnamnesis * 0.002)
          .toFixed(2),
  );
};

const calculateElasticSearchCost = (
    row: TelemedicineCharges.RowData,
): number => {
  return Number((100 / row.rowsLength).toFixed(2));
};

const calculateTotalCost = (
    row: TelemedicineCharges.RowData,
): number => {
  const chimeCost = calculateChimeCost(row);
  const elasticSearchCost = calculateElasticSearchCost(row);
  const googleCloudCost = calculateGCPIntegrationCost(row);

  const totalCost = (
    chimeCost +
      elasticSearchCost +
      googleCloudCost
  )
      .toFixed(2);

  return Number(totalCost);
};

const CostModal = (
    props: RowProps,
) => {
  const {
    row,
  } = props;

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <IconButton
        aria-label="expand row"
        size="small"
        onClick={() => handleOpen()}
      >
        <RequestQuoteIcon/>
      </IconButton>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1000,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h4" gutterBottom component="div">
            Custos de integração: {row.clientName}
          </Typography>
          <Table size="small" aria-label="billing">
            <TableHead>
              <TableRow>
                <TableCell>Informação</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow key={`billing-estimate-${row.projectId}`}>
                <TableCell component="th" scope="row">
                  Custo configurado
                </TableCell>
                <TableCell align="right">
                  U$ {row.billingEstimate.toFixed(2)}
                </TableCell>
              </TableRow>
              <TableRow key={`billing-usage-${row.projectId}`}>
                <TableCell component="th" scope="row">
                  Utilização geral
                </TableCell>
                <TableCell align="right">
                  {row.billingUsagePercent}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Modal>
    </>
  );
};

const columns = [
  {
    title: 'Nome do cliente',
    field: 'clientName',
    type: 'string',
    sorting: true,
  },
  {
    title: 'ID do cliente',
    field: 'projectId',
    type: 'string',
    sorting: true,
  },
  {
    title: 'Atendimentos finalizados',
    field: 'totalFinishedAnamnesis',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Atendimentos iniciados',
    field: 'totalStartedAnamnesis',
    type: 'numeric',
    sorting: true,
    defaultSort: 'desc',
  },
  {
    title: 'Média de tempo por atendimento(min)',
    field: 'averageAnamnesisDurationInMinutes',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Médicos cadastrados',
    field: 'totalHealthProfessionals',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Médicos ativos',
    field: 'totalActiveHealthProfessionals',
    type: 'string',
    sorting: true,
  },
  {
    title: 'SMS utilizados',
    field: 'totalSms',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Chamadas de API por integração',
    field: 'totalIntegrationAPICalls',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Gravação do período selecionado(GB)',
    field: 'recordingSizeInGb',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Gravação total(GB)',
    field: 'totalRecordingSizeInGb',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Custos de integração',
    field: 'billing',
    render: (row) => {
      return (
        <CostModal row={row}/>
      );
    },
  },
  {
    title: '(AWS) Chime(U$)',
    field: 'chimeCost',
    type: 'numeric',
    sorting: true,
  },
  {
    title: '(AWS) EC2/ElasticSearch(U$)',
    field: 'awsElasticSearchCost',
    type: 'numeric',
    sorting: true,
  },
  {
    title: '(GCP) Integração de API(U$)',
    field: 'gcpIntegrationApiCost',
    type: 'numeric',
    sorting: true,
  },
  {
    title: 'Valor Total(U$)',
    field: 'totalCost',
    type: 'numeric',
    sorting: true,
  },
] as TelemedicineCharges.Column[];

interface TelemedicineTableChargesProps {
  rows: TelemedicineCharges.RowData[]
}

const TelemedicineTableCharges = (
    props: TelemedicineTableChargesProps,
) => {
  const {rows} = props;

  const defaultMaterialTheme = createTheme();

  return (
    <div style={{maxWidth: '100%'}}>
      <ThemeProvider theme={defaultMaterialTheme}>
        <CustomMaterialTable
          title=""
          columns={columns}
          data={
            rows.map((row) => ({
              ...row,
              rowsLength: rows.length,
              chimeCost: calculateChimeCost(row),
              awsElasticSearchCost: calculateElasticSearchCost(row),
              gcpIntegrationApiCost: calculateGCPIntegrationCost(row),
              totalCost: calculateTotalCost(row),
            }))
          }
          options={{
            exportButton: {
              csv: true,
            },
          }}
        />
      </ThemeProvider>
    </div>
  );
};

export default TelemedicineTableCharges;

